import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, {
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

import { useColor } from '@/hooks/useColor';

import PredictionButton from '../buttons/PredictionButton';
import Text from '../Text';
import { OptionType } from './types';

export type DropdownInputFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  options: OptionType[];
  label?: string;
  placeholder?: string;
  variant?: 'default' | 'rounded_with_shadow';
  showError?: boolean;
};

const DropdownInputField = <T extends FieldValues>({
  control,
  name,
  options,
  label,
  placeholder,
  variant = 'default',
  showError = true
}: DropdownInputFieldProps<T>) => {
  const colors = useColor();
  const [isOpen, setIsOpen] = useState(false);

  // Animated measurements
  const listRef = useAnimatedRef<View>();
  const heightValue = useSharedValue(0);
  const openValue = useSharedValue(false);
  const isAnimating = useSharedValue(false);
  const progress = useDerivedValue(() => (openValue.value ? withTiming(1) : withTiming(0)));

  const heightAnimationStyle = useAnimatedStyle(() => ({
    height: heightValue.value,
    opacity: withTiming(openValue.value ? 1 : 0)
  }));

  const iconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${progress.value * 180}deg` }]
  }));

  const onToggle = () => {
    runOnUI(() => {
      'worklet';
      if (isAnimating.value) return;
      isAnimating.value = true;
      if (heightValue.value === 0) {
        const measured = measure(listRef);
        if (measured) {
          heightValue.value = withTiming(measured.height, {}, () => {
            isAnimating.value = false;
          });
        } else {
          isAnimating.value = false;
        }
      } else {
        heightValue.value = withTiming(0, {}, () => {
          isAnimating.value = false;
        });
      }
      openValue.value = !openValue.value;
    })();
    setIsOpen(!isOpen);
  };

  const inputHeight = variant === 'default' ? 56 : 38;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const selectedOption = options.find((opt) => opt.value === value);

        return (
          <View style={styles.container}>
            {label && (
              <Text fontWeight="700" style={styles.label}>
                {label}
              </Text>
            )}

            <View
              style={[
                variant === 'default' ? styles.defaultInput : styles.roundedWithShadow,
                isOpen && styles.openContainer,
                error && { borderColor: 'red', borderWidth: 1 }
              ]}>
              <Pressable style={[styles.inputPressable, { height: inputHeight }]} onPress={onToggle}>
                <Text style={[styles.valueText, !selectedOption && { color: colors.placeholderText }]}>
                  {selectedOption ? selectedOption.label : placeholder}
                </Text>
                <Animated.View style={iconStyle}>
                  <Ionicons name="chevron-down" size={20} color="gray" />
                </Animated.View>
              </Pressable>

              <Animated.View style={[heightAnimationStyle, { overflow: 'hidden' }]}>
                <View ref={listRef} style={styles.listContainer}>
                  <ScrollView style={{ maxHeight: 200 }} showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
                    {options.map((item, index) => (
                      <View key={`${item.value}-${index}`}>
                        <PredictionButton
                          item={{ label: item.label, value: item.value }}
                          onPredictionTapped={(prediction) => {
                            onChange(prediction.value);
                            onToggle();
                          }}
                        />
                        {index < options.length - 1 && <Divider />}
                      </View>
                    ))}
                  </ScrollView>
                </View>
              </Animated.View>
            </View>

            {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
          </View>
        );
      }}
    />
  );
};

export default DropdownInputField;

const styles = StyleSheet.create({
  container: {
    marginVertical: 5,
    zIndex: 1000
  },

  label: {
    color: 'black',
    marginBottom: 10
  },

  defaultInput: {
    borderWidth: 1,
    borderRadius: 8,
    borderColor: 'lightgray',
    backgroundColor: 'white'
  },

  roundedWithShadow: {
    backgroundColor: 'white',
    borderRadius: 12,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5,
    zIndex: 1000
  },

  openContainer: {
    borderRadius: 15
  },

  inputPressable: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16
  },

  valueText: {
    fontSize: 16,
    color: 'black'
  },

  listContainer: {
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    paddingBottom: 5
  },

  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'red'
  }
});
